import React, { useState } from 'react';
import { Avatar, Box, Dialog, useTheme } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { AccountData } from '../api';
import { useAuth } from '../session/InternalAuthProvider';

interface CatalogItemPhotoProps {
  photoUrl?: string | null;
  alt?: string;
  size: number | 'fill';
  lazy?: boolean;
  enlargeOnClick?: boolean;
}

interface CatalogPhotoLightboxProps {
  photoUrl: string;
  alt?: string;
  open: boolean;
  onClose: () => void;
}

export const CatalogPhotoLightbox: React.FC<CatalogPhotoLightboxProps> = ({ photoUrl, alt, open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth={false} onClick={(event) => event.stopPropagation()}>
    <Box onClick={onClose} sx={{ cursor: 'zoom-out', lineHeight: 0, backgroundColor: 'common.black' }}>
      <Box
        component="img"
        src={photoUrl}
        alt={alt}
        sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
      />
    </Box>
  </Dialog>
);

export const CatalogItemPhoto: React.FC<CatalogItemPhotoProps> = ({
  photoUrl,
  alt,
  size,
  lazy,
  enlargeOnClick = true,
}) => {
  const [isEnlarged, setIsEnlarged] = useState(false);
  const theme = useTheme();
  const { authState } = useAuth();
  const accountLogo = (authState.account as unknown as AccountData)?.logo || undefined;

  const resolvedUrl = photoUrl || accountLogo;
  const isLogoFallback = !photoUrl && !!accountLogo;
  const canEnlarge = enlargeOnClick && !!photoUrl;
  const dimensions =
    size === 'fill' ? { width: '100%', aspectRatio: '1 / 1', height: 'auto' } : { width: size, height: size };
  const iconSize = size === 'fill' ? '3rem' : size * 0.5;

  if (!resolvedUrl) {
    return (
      <Avatar variant="rounded" sx={{ ...dimensions, bgcolor: 'mb.ground', color: 'text.disabled' }}>
        <Inventory2OutlinedIcon sx={{ fontSize: iconSize }} />
      </Avatar>
    );
  }

  return (
    <>
      <Avatar
        variant="rounded"
        src={resolvedUrl}
        alt={alt}
        imgProps={{
          loading: lazy ? 'lazy' : undefined,
          style: {
            objectFit: isLogoFallback ? 'contain' : 'cover',
            boxSizing: 'border-box',
            padding: isLogoFallback ? '12%' : 0,
            backgroundColor: isLogoFallback ? theme.palette.background.paper : undefined,
          },
        }}
        onClick={
          canEnlarge
            ? (event) => {
                event.stopPropagation();
                setIsEnlarged(true);
              }
            : undefined
        }
        sx={{ ...dimensions, cursor: canEnlarge ? 'zoom-in' : undefined }}
      />
      {photoUrl && (
        <CatalogPhotoLightbox photoUrl={photoUrl} alt={alt} open={isEnlarged} onClose={() => setIsEnlarged(false)} />
      )}
    </>
  );
};
